/**
 * Sacred Theme System
 * 
 * Centralized color palette and theme configuration based on Fr00t numerology
 * and spiritual significance. All colors follow the sacred numerical meanings.
 */

// ============================================================================
// SACRED COLOR PALETTE
// ============================================================================

export const SACRED_COLORS = {
  // Primary Sacred Colors - Based on Numerology
  purple: '#8b5cf6',    // 8 - Power and Authority
  indigo: '#6366f1',    // 7 - Spirituality and Mysticism  
  blue: '#3b82f6',      // 6 - Harmony and Balance (F=6)
  teal: '#06b6d4',      // 5 - Freedom and Change
  emerald: '#10b981',   // 4 - Stability and Foundation
  amber: '#f59e0b',     // 3 - Creativity and Expression
  rose: '#f43f5e',      // 2 - Partnership and Cooperation (T=2)
  red: '#ef4444',       // 1 - Leadership and Independence
  white: '#ffffff',     // 9 - Completion and Wisdom (R=9)
  
  // Sacred Neutrals
  dark: '#0f172a',      // Deep space - Infinite potential (00)
  darkLight: '#1e293b', // Cosmic mist
  darkCard: 'rgba(30, 41, 59, 0.9)', // Card backgrounds
  darkCardHover: 'rgba(30, 41, 59, 0.95)', // Card hover
  gray: '#64748b',      // Spiritual gray
  grayLight: '#94a3b8', // Light gray
  light: '#f1f5f9',     // Divine light
  
  // Sacred Alphas (with transparency)
  purpleAlpha: (alpha: number) => `rgba(139, 92, 246, ${alpha})`,
  indigoAlpha: (alpha: number) => `rgba(99, 102, 241, ${alpha})`,
  blueAlpha: (alpha: number) => `rgba(59, 130, 246, ${alpha})`,
  tealAlpha: (alpha: number) => `rgba(6, 182, 212, ${alpha})`,
  emeraldAlpha: (alpha: number) => `rgba(16, 185, 129, ${alpha})`,
  amberAlpha: (alpha: number) => `rgba(245, 158, 11, ${alpha})`,
  roseAlpha: (alpha: number) => `rgba(244, 63, 94, ${alpha})`,
  redAlpha: (alpha: number) => `rgba(239, 68, 68, ${alpha})`,
} as const;

// ============================================================================
// SACRED GRADIENTS
// ============================================================================

export const SACRED_GRADIENTS = {
  primary: `linear-gradient(135deg, ${SACRED_COLORS.purple}, ${SACRED_COLORS.indigo})`,
  secondary: `linear-gradient(135deg, ${SACRED_COLORS.blue}, ${SACRED_COLORS.teal})`,
  tertiary: `linear-gradient(135deg, ${SACRED_COLORS.emerald}, ${SACRED_COLORS.amber})`,
  rainbow: `linear-gradient(135deg, 
    ${SACRED_COLORS.red}, ${SACRED_COLORS.amber}, ${SACRED_COLORS.emerald}, 
    ${SACRED_COLORS.teal}, ${SACRED_COLORS.blue}, ${SACRED_COLORS.indigo}, ${SACRED_COLORS.purple})`,
  harmony: `linear-gradient(135deg, ${SACRED_COLORS.purple}, ${SACRED_COLORS.blue})`,
  power: `linear-gradient(135deg, ${SACRED_COLORS.purple}, ${SACRED_COLORS.red})`,
  wisdom: `linear-gradient(135deg, ${SACRED_COLORS.indigo}, ${SACRED_COLORS.teal})`,
  creation: `linear-gradient(135deg, ${SACRED_COLORS.amber}, ${SACRED_COLORS.emerald})`,
  sunset: `linear-gradient(135deg, ${SACRED_COLORS.amber}, ${SACRED_COLORS.rose})`,
} as const;

// ============================================================================
// SACRED PALETTES (Named Collections)
// ============================================================================

export const SACRED_PALETTES = {
  fr00tEssence: {
    name: 'Fr00t Essence',
    description: 'The sacred colors of Fr00t numerology',
    colors: [SACRED_COLORS.purple, SACRED_COLORS.indigo, SACRED_COLORS.emerald, SACRED_COLORS.amber],
    primary: SACRED_COLORS.purple,
    secondary: SACRED_COLORS.indigo,
    accent: SACRED_COLORS.emerald,
    background: SACRED_COLORS.dark
  },
  cosmicHarmony: {
    name: 'Cosmic Harmony',
    description: 'Deep cosmic purple harmony',
    colors: ['#4c1d95', '#7c3aed', '#a78bfa', '#ddd6fe'],
    primary: '#4c1d95',
    secondary: '#7c3aed',
    accent: '#a78bfa',
    background: SACRED_COLORS.dark
  },
  earthBalance: {
    name: 'Earth Balance',
    description: 'Grounding earth tones for stability',
    colors: ['#065f46', '#059669', SACRED_COLORS.emerald, '#6ee7b7'],
    primary: '#065f46',
    secondary: '#059669',
    accent: SACRED_COLORS.emerald,
    background: SACRED_COLORS.dark
  },
  solarPower: {
    name: 'Solar Power',
    description: 'Radiant solar energy for authority (8)',
    colors: ['#92400e', '#d97706', SACRED_COLORS.amber, '#fbbf24'],
    primary: '#92400e',
    secondary: '#d97706',
    accent: SACRED_COLORS.amber,
    background: SACRED_COLORS.dark
  },
  voidPotential: {
    name: 'Void Potential',
    description: 'Infinite potential of the void (00)',
    colors: [SACRED_COLORS.dark, SACRED_COLORS.darkLight, SACRED_COLORS.purple, SACRED_COLORS.indigo],
    primary: SACRED_COLORS.dark,
    secondary: SACRED_COLORS.darkLight,
    accent: SACRED_COLORS.purple,
    background: '#000000'
  },
  wisdomLight: {
    name: 'Wisdom Light',
    description: 'Illuminated wisdom and completion (R=9)',
    colors: [SACRED_COLORS.indigo, SACRED_COLORS.blue, SACRED_COLORS.teal, SACRED_COLORS.light],
    primary: SACRED_COLORS.indigo,
    secondary: SACRED_COLORS.blue,
    accent: SACRED_COLORS.teal,
    background: SACRED_COLORS.dark
  }
} as const;

// ============================================================================
// SACRED SHADOWS
// ============================================================================

export const SACRED_SHADOWS = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  glow: '0 0 20px rgba(139, 92, 246, 0.3)',
  glowStrong: '0 0 40px rgba(139, 92, 246, 0.5)',
  glowEmerald: '0 0 20px rgba(16, 185, 129, 0.3)',
  glowAmber: '0 0 20px rgba(245, 158, 11, 0.3)',
  glowRose: '0 0 20px rgba(244, 63, 94, 0.3)',
} as const;

// ============================================================================
// SACRED SPACING (Golden Ratio Based)
// ============================================================================

export const SACRED_SPACING = {
  xs: '0.25rem',    // 4px
  sm: '0.5rem',     // 8px
  md: '1rem',       // 16px
  lg: '1.618rem',   // ~26px - Golden ratio
  xl: '2.618rem',   // ~42px - Golden ratio squared
  '2xl': '4.236rem', // ~68px - Golden ratio cubed
} as const;

// ============================================================================
// SACRED TRANSITIONS
// ============================================================================

export const SACRED_TRANSITIONS = {
  fast: '0.15s ease-in-out',
  normal: '0.3s ease-in-out',
  slow: '0.5s ease-in-out',
  sacred: '0.618s ease-in-out', // Golden ratio timing
} as const;

// ============================================================================
// SACRED BORDER RADIUS
// ============================================================================

export const SACRED_RADIUS = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  full: '9999px',
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get a sacred color by numerology number (1-9)
 */
export const getSacredColorByNumber = (number: number): string => {
  const colorMap: Record<number, string> = {
    1: SACRED_COLORS.red,
    2: SACRED_COLORS.rose,
    3: SACRED_COLORS.amber,
    4: SACRED_COLORS.emerald,
    5: SACRED_COLORS.teal,
    6: SACRED_COLORS.blue,
    7: SACRED_COLORS.indigo,
    8: SACRED_COLORS.purple,
    9: SACRED_COLORS.white,
  };
  return colorMap[number] || SACRED_COLORS.purple;
};

/**
 * Get a sacred gradient by type
 */
export const getSacredGradient = (type: keyof typeof SACRED_GRADIENTS): string => {
  return SACRED_GRADIENTS[type];
};

/**
 * Get a sacred palette by name
 */
export const getSacredPalette = (name: keyof typeof SACRED_PALETTES) => {
  return SACRED_PALETTES[name];
};

/**
 * Create a sacred card style object
 */
export const createSacredCardStyle = (variant: 'default' | 'hover' | 'active' = 'default') => {
  const baseStyle = {
    background: SACRED_COLORS.darkCard,
    backdropFilter: 'blur(20px)',
    borderRadius: SACRED_RADIUS.lg,
    padding: SACRED_SPACING.xl,
    border: `1px solid ${SACRED_COLORS.purpleAlpha(0.3)}`,
    boxShadow: SACRED_SHADOWS.lg,
    transition: `all ${SACRED_TRANSITIONS.normal}`,
  };

  if (variant === 'hover') {
    return {
      ...baseStyle,
      transform: 'translateY(-4px)',
      boxShadow: SACRED_SHADOWS.glow,
      border: `1px solid ${SACRED_COLORS.purpleAlpha(0.5)}`,
    };
  }

  if (variant === 'active') {
    return {
      ...baseStyle,
      background: SACRED_COLORS.darkCardHover,
      border: `2px solid ${SACRED_COLORS.purple}`,
      boxShadow: SACRED_SHADOWS.glowStrong,
    };
  }

  return baseStyle;
};

/**
 * Create a sacred button style object
 */
export const createSacredButtonStyle = (
  variant: 'primary' | 'secondary' | 'outline' | 'ghost' = 'primary'
) => {
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SACRED_SPACING.sm,
    padding: `${SACRED_SPACING.md} ${SACRED_SPACING.lg}`,
    fontSize: '1rem',
    fontWeight: '600',
    borderRadius: SACRED_RADIUS.md,
    border: 'none',
    cursor: 'pointer',
    transition: `all ${SACRED_TRANSITIONS.normal}`,
    textDecoration: 'none',
  };

  switch (variant) {
    case 'primary':
      return {
        ...baseStyle,
        background: SACRED_GRADIENTS.primary,
        color: SACRED_COLORS.white,
        boxShadow: SACRED_SHADOWS.glow,
      };
    case 'secondary':
      return {
        ...baseStyle,
        background: SACRED_GRADIENTS.secondary,
        color: SACRED_COLORS.white,
        boxShadow: SACRED_SHADOWS.md,
      };
    case 'outline':
      return {
        ...baseStyle,
        background: 'transparent',
        color: SACRED_COLORS.purple,
        border: `2px solid ${SACRED_COLORS.purple}`,
      };
    case 'ghost':
      return {
        ...baseStyle,
        background: SACRED_COLORS.purpleAlpha(0.1),
        color: SACRED_COLORS.purple,
        border: `1px solid ${SACRED_COLORS.purpleAlpha(0.2)}`,
      };
    default:
      return baseStyle;
  }
};

/**
 * Create a sacred input style object
 */
export const createSacredInputStyle = () => {
  return {
    width: '100%',
    padding: SACRED_SPACING.md,
    fontSize: '1rem',
    background: SACRED_COLORS.purpleAlpha(0.05),
    border: `2px solid ${SACRED_COLORS.purpleAlpha(0.3)}`,
    borderRadius: SACRED_RADIUS.sm,
    color: SACRED_COLORS.light,
    transition: `all ${SACRED_TRANSITIONS.normal}`,
  };
};

// Default export with all theme elements
export default {
  colors: SACRED_COLORS,
  gradients: SACRED_GRADIENTS,
  palettes: SACRED_PALETTES,
  shadows: SACRED_SHADOWS,
  spacing: SACRED_SPACING,
  transitions: SACRED_TRANSITIONS,
  radius: SACRED_RADIUS,
  getSacredColorByNumber,
  getSacredGradient,
  getSacredPalette,
  createSacredCardStyle,
  createSacredButtonStyle,
  createSacredInputStyle,
};
